import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "components/shared";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import { ReactComponent as DocumentIcon } from "images/documentIcon.svg";
import { ReactComponent as DownloadIcon } from "images/downloadArrowOnCircleFilled.svg";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { IMarker } from "toolkit/organizations/types";
import Marker from "./Marker";
import Badge from "components-new/Badge";
import NewCheckBox from "components-new/NewCheckBox";
import { setDocumentKii, setMarkers } from "toolkit/organizations/organizations.thunks";
import { isEmpty } from "helpers";
import cn from "classnames";
import styles from "./SettingMarkers.module.sass";
import { directoryAPI } from "libs/apiClient/Directory";
import { filesAPI } from "libs/apiClient/Files";

interface SettingMarkersProps {
	close: () => void;
	setDocumentName?: any;
	setPath?: any;
	documentName?: string | null;
	path?: any;
}

export const SettingMarkers: FC<SettingMarkersProps> = ({ close, setDocumentName, setPath }) => {
	const dispatch = useAppDispatch();
	const [systemId, setSystemId] = useState<number | null>(null);

	const [addedFile, setAddedFile] = useState("");

	const { selectedOrganization } = useAppSelector((state) => state.organizations);
	const markerList = useAppSelector((state) => state.organizations.markerList);

	const [markerListLocal, setMarkerListLocal] = useState<IMarker[]>([]);
	const [systemLocal, setSystemLocal] = useState<any>();

	const [criticalStructure, setCriticalStructure] = useState<{ value: boolean; file: File | null }>({
		value: false,
		file: null,
	});

	const submitHandler = () => {
		const sendingMarkers = markerListLocal
			.filter((_) => _.link)
			.map((_) => {
				const { link, ...marker } = _;
				return marker;
			});
		if (criticalStructure.value) {
			dispatch(
				setMarkers({
					id: Number(selectedOrganization?.id),
					markers: [...sendingMarkers, criticalStructure.value && { id: systemId }],
				}),
			);
			dispatch(
				setDocumentKii({ orgId: selectedOrganization?.id, file: criticalStructure.file, setDocumentName, setPath }),
			);
		} else {
			dispatch(
				setMarkers({
					id: Number(selectedOrganization?.id),
					markers: sendingMarkers,
				}),
			);
			dispatch(
				setDocumentKii({ orgId: selectedOrganization?.id, file: criticalStructure.file, setDocumentName, setPath }),
			);
		}

		close();
	};
	const markersIDs = useMemo(() => {
		setCriticalStructure((prevState) => ({ ...prevState, value: !isEmpty(markerList.system) }));

		return markerList.user.map((_) => _.id);
	}, [markerList]);
	useEffect(() => {
		directoryAPI.organizationsMarkersList().then(({ data }) => {
			setSystemId(data.system.KII.id || null);
			setMarkerListLocal(
				data.user.map((_) => ({
					..._,
					link: markersIDs.indexOf(_.id) !== -1,
				})),
			);
			setSystemLocal(data.system?.KII);
		});
		//eslint-disable-next-line
	}, []);

	const changeMarker = (markerId: number) => {
		const newMarkerList = [...markerListLocal];
		const markerIndex = markerListLocal.findIndex((marker) => marker.id === markerId);
		newMarkerList[markerIndex].link = !newMarkerList[markerIndex].link;
		setMarkerListLocal(newMarkerList);
	};

	const handleFile = (e: any) => {
		setAddedFile(e.target.files[0].name);
		setCriticalStructure((prevState) => ({ ...prevState, file: e.target.files[0] }));
	};

	const downloadFile = useCallback(async () => {
		let href = await filesAPI
			.list(String(selectedOrganization?.kiiDocument?.key), { format: "blob" })
			.then(({ data }) => URL.createObjectURL(data));

		if (href) {
			// create "a" HTML element with href to file & click
			const link = document.createElement("a");
			link.href = href;
			link.target = "_blank";
			link.setAttribute("download", `${selectedOrganization?.kiiDocument?.name}`); //or any other extension
			document.body.appendChild(link);
			link.click();

			// clean up "a" element & remove ObjectURL
			document.body.removeChild(link);
			URL.revokeObjectURL(href);
		}
	}, [selectedOrganization?.kiiDocument]);

	return (
		<section className={styles.wrp}>
			<h4 className={styles.title}>Маркеры организации</h4>

			<div className={styles["markers-wrp"]}>
				<div className={styles["markers-wrp-top"]}>
					<label className={styles.cursor}>
						<Badge>
							<div className={cn(styles.labelBadge, styles.cursor)}>
								<NewCheckBox
									type="checkbox"
									onChange={() =>
										setCriticalStructure((prevState) => ({
											...prevState,
											value: !prevState.value,
										}))
									}
									checked={criticalStructure.value}
								/>
								<span className={styles.markerText}>{markerList.system?.name || systemLocal?.name}</span>
							</div>
						</Badge>
					</label>

					<div className={styles.addDocBtn}>
						<label className={cn(styles.uploadDoc)}>
							<input type="file" className={styles.file} onChange={handleFile} />
							<DocumentIcon className={styles.documentIcon} />
							{selectedOrganization?.kiiDocument?.name ? (
								<span className={styles.docName}>{addedFile || selectedOrganization?.kiiDocument?.name}</span>
							) : (
								"Прикрепить документ"
							)}
						</label>
						{!addedFile && selectedOrganization?.kiiDocument?.name && (
							<DownloadIcon className={styles.downloadIcon} onClick={downloadFile} />
						)}
					</div>
				</div>
				<span className={styles["markers-title"]}>Маркеры организации</span>
				<div className={styles.markers}>
					{markerListLocal.length ? (
						markerListLocal.map((marker) => <Marker key={marker.id} marker={marker} onChange={changeMarker} />)
					) : (
						<span className={styles["markers-warning"]}>Нет доступных маркеров</span>
					)}
				</div>
			</div>
			<div className={styles.btns}>
				<Button onClick={close} type="button" variant={BUTTON_VARIANTS.OUTLINED} className={styles.button}>
					Отменить
				</Button>
				<Button
					disabled={criticalStructure.value && false}
					onClick={submitHandler}
					type="button"
					className={styles.button}>
					Готово
				</Button>
			</div>
		</section>
	);
};
